import { createColumnHelper, CellContext } from '@tanstack/react-table';
import { lt, showSuccessAlert } from '@mews/framework';
import { Typography, Button } from '@optimus-web/core';
import { TextKey } from '@mews-commander/enums';
import { SwedishCashRegisterFiscalDayGetAllResult, useRepositoryMutation } from '@mews-commander/data-access';
import { LocalizedLongDateFromUtc } from '@mews-commander/ui';
import { useGetTodayISO } from './useGetTodayISO';

export type SwedishTableReport = SwedishCashRegisterFiscalDayGetAllResult[number];

// Standalone cell components
const DateCell: React.FC<CellContext<SwedishTableReport, string>> = (info) => {
    const date = info.getValue();
    // Only show date for first row in a date group
    const rowIndex = info.row.index;
    const prevRowDate = rowIndex > 0 ? info.table.getRowModel().rows[rowIndex - 1].original.date : null;

    if (rowIndex === 0 || prevRowDate !== date) {
        return (
            <Typography as="div" textStyle="bodyMediumStrong">
                <LocalizedLongDateFromUtc utcString={date} />
            </Typography>
        );
    }
    return null; // Return nothing for repeated dates
};

const ReportCell: React.FC<CellContext<SwedishTableReport, unknown>> = ({ row }) => {
    const report = row.original;

    const todayISO = useGetTodayISO();
    const isToday = report.date === todayISO;
    const buttonLabel = isToday ? lt(TextKey.SwedishXReport) : lt(TextKey.SwedishZReport);
    const methodName = isToday ? 'exportXReport' : 'exportZReport';

    const { mutate, isPending } = useRepositoryMutation('swedishCashRegisterFiscalDay', methodName, {
        onSuccess: () => {
            showSuccessAlert({
                description: lt(TextKey.ExportQueuedSuccessMessage),
            });
        },
    });

    return (
        <Button
            variant="tertiary"
            size="small"
            onClick={() =>
                mutate({
                    date: report.date,
                    integrationId: report.integrationId,
                    swedishCashRegisterId: report.swedishCashRegister.id,
                })
            }
            type="button"
            loading={isPending}
        >
            {buttonLabel}
        </Button>
    );
};

const columnHelper = createColumnHelper<SwedishTableReport>();

export const simpleColumns = [
    columnHelper.accessor('date', {
        header: () => lt(TextKey.Date),
        cell: DateCell,
    }),
    columnHelper.accessor('swedishCashRegister.name', {
        header: () => lt(TextKey.SwedishCashRegister),
    }),
    columnHelper.accessor('fiscalDayNumber', {
        header: () => lt(TextKey.SerialNumber),
    }),
    columnHelper.display({
        id: 'report',
        header: () => lt(TextKey.ReportPlural),
        cell: ReportCell,
    }),
];
